import { AccessLevel } from '@upright/common/iam/access/types';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { MeasurableGroupType } from '@upright/common/model/input/measurable-group/types';
import { PossiblyNamedModelEntityReference } from '@upright/common/model/input/types';
import { ValueSet } from '@upright/common/model/input/value-sets/types';
import { FinalImpactScoreType, NetScoreFormat } from '@upright/common/model/output/scores/types';
import { RequestStatus } from '@upright/ui/api/common/types';
import { ImpactLens } from '@upright/ui/common/upright/core/impact-lenses/types';
import { LensSelectionStateHandler } from '../impact-lenses/selection-hook';

/**
 * Lens content contains data and state relevant for a given lens
 */
export interface LensData<T extends ImpactLensType = ImpactLensType> {
  type: T;
  loadingStatus: RequestStatus;
  access: LensDataAccess;
  availability: LensDataAvailability;
  entity?: PossiblyNamedModelEntityReference;
  accessLevel?: AccessLevel; // TODO: Delete this duplication of access logic and move to be part of LensDataAccess
  source?: string;
}

export type LensDataAccessDetails = {
  access: LensDataAccess;
  availability: LensDataAvailability;
};

export enum LensDataAccess {
  GRANTED,
  DENIED,
  UNKNOWN,
}

export enum LensDataAvailability {
  AVAILABLE,
  UNAVAILABLE, // i.e. "no data"
  UNKNOWN,
}

/**
 * Common content for single-data series lenses
 * (we will later on add support for also
 *  multi-data series lenses)
 */
export interface SingleEntityLensData<T extends ImpactLensType> extends LensData<T> {
  type: T;
  entity?: PossiblyNamedModelEntityReference;
  groupType?: MeasurableGroupType;
}

export interface ImpactLensProfileProps<
  A extends ImpactLensType,
  B extends LensData<A>,
  C extends LensOptions<A>,
  D extends ImpactLens<A, B, C>,
> {
  lens: D;
  data: B;
  options?: Partial<C>;
  width: number;
  lensIndex: number;
}

export interface LensOptions<T extends ImpactLensType = ImpactLensType> {
  type: T;
  disableImpactTitleInteraction?: boolean;
  disableStateClearingOnOutsideClick?: boolean;
  hideClearSelectionButton?: boolean;
  hideTopDescriptionArea?: boolean;
  hideTopRightActions?: boolean;
  hideTooltips?: boolean;
  selectionStateHandler?: LensSelectionStateHandler; // If not provided, the selection state will be handled in the URL in useUrlSelectionState
  netScoreFormat?: NetScoreFormat;
  valueSetDefinition?: ValueSet;
  scoreUnit?: FinalImpactScoreType;
}
